
import { Vue, Component } from 'vue-property-decorator'
import axios from 'axios'
import { message, notification } from 'ant-design-vue'
import moment from 'moment'

interface SurveyType {
  type: string
  project_id: number|null
  block_ids: number[]
  building_ids: number[]
  survey_form_ids: number[]
  scheduled_user_id: number|null
  scheduled_at: moment.Moment|null
  agree: boolean
}

interface BlockType {
  id: number
  name: string
  number: number
}

interface BuildingType {
  id: number
  reference: string
  number: number
}

@Component({
  methods: {},
  components: {}
})

export default class SurveyBulkCreator extends Vue {
  showModal = false
  loading = { projects: false, blocks: false, buildings: false, forms: false, users: false }
  saving = false

  projects = []
  blocks = []
  buildings = []
  forms = []
  users = []

  surveyData:SurveyType = {
    type: 'project',
    project_id: null,
    block_ids: [],
    building_ids: [],
    survey_form_ids: [],
    scheduled_user_id: null,
    scheduled_at: null,
    agree: false
  }

  toggleModal () : void {
    this.showModal = !this.showModal

    if (this.showModal) {
      this.getProjects()
      this.getForms()
    } else {
      this.surveyData = {
        type: 'project',
        project_id: null,
        block_ids: [],
        building_ids: [],
        survey_form_ids: [],
        scheduled_user_id: null,
        scheduled_at: null,
        agree: false
      }
    }
  }

  typeChanged () : void {
    this.surveyData.block_ids = []
    this.surveyData.building_ids = []
    this.surveyData.survey_form_ids = []

    if (this.surveyData.type === 'block') {
      this.getBlocks()
    }

    if (this.surveyData.type === 'building') {
      this.getBuildings()
    }

    this.getForms()
  }

  getProjects () : void {
    this.loading.projects = true
    this.surveyData.project_id = null
    this.surveyData.block_ids = []
    this.surveyData.building_ids = []
    this.surveyData.scheduled_user_id = null
    this.surveyData.agree = false

    axios.get(process.env.VUE_APP_API_URL + '/projects?filter[phase]=1,2,3,4&limit=500').then((r) => {
      this.projects = r.data.data
    }).catch(() => {
      message.error('Fout tijdens het ophalen van projecten!')
    }).finally(() => {
      this.loading.projects = false
    })
  }

  projectChanged () : void {
    this.getUsers()

    if (this.surveyData.type === 'block') {
      this.getBlocks()
    } else if (this.surveyData.type === 'building') {
      this.getBuildings()
    }
  }

  getBlocks () : void {
    if (this.surveyData.project_id === null) return

    this.loading.blocks = true
    this.surveyData.block_ids = []
    this.surveyData.building_ids = []
    this.surveyData.agree = false

    axios.get(process.env.VUE_APP_API_URL + '/buildingblocks?filter[project_id]=' + this.surveyData.project_id + '&sort=number&limit=500').then((r) => {
      this.blocks = r.data.data
    }).catch(() => {
      message.error('Fout tijdens het ophalen van blokken!')
    }).finally(() => {
      this.loading.blocks = false
    })
  }

  getBuildings () : void {
    if (this.surveyData.project_id === null) return

    this.loading.buildings = true
    this.surveyData.block_ids = []
    this.surveyData.building_ids = []
    this.surveyData.agree = false

    axios.get(process.env.VUE_APP_API_URL + '/buildings?filter[project_id]=' + this.surveyData.project_id + '&sort=number&limit=500').then((r) => {
      this.buildings = r.data.data
    }).catch(() => {
      message.error('Fout tijdens het ophalen van bouwnummers!')
    }).finally(() => {
      this.loading.buildings = false
    })
  }

  getForms () : void {
    this.loading.forms = true
    const objectTypeMap:{ [key:string]: string } = {
      project: '1',
      block: '4',
      building: '2,3'
    }

    const objectType = objectTypeMap[this.surveyData.type]

    axios.get(process.env.VUE_APP_API_URL + '/surveys/forms?filter[type]=1,2,3,5,7,9,10&filter[object_type]=' + objectType + '&filter[active]=1&sort=reference&limit=2500').then((r) => {
      this.forms = r.data.data
    }).catch(() => {
      message.error('Fout tijdens het ophalen van keuringsformulieren!')
    }).finally(() => {
      this.loading.forms = false
    })
  }

  getUsers () : void {
    this.loading.users = true
    this.surveyData.scheduled_user_id = null

    axios.get(process.env.VUE_APP_API_URL + '/members/' + this.surveyData.project_id + '/surveyInspectors').then((r) => {
      this.users = r.data
    }).catch(() => {
      message.error('Fout tijdens het ophalen van medewerkers!')
    }).finally(() => {
      this.loading.users = false
    })
  }

  addSurveys () : void {
    this.saving = true

    const data = {
      ...this.surveyData,
      scheduled_at: this.surveyData.scheduled_at ? moment(this.surveyData.scheduled_at).format('YYYY-MM-DD HH:mm:ss') : null
    }

    axios.post(process.env.VUE_APP_API_URL + '/surveys/bulk/add', data).then(() => {
      this.surveyData.agree = false
      this.$emit('surveysAdded')
      notification.success({
        message: 'Keuringen toegevoegd.',
        description: 'De keuringen zijn succesvol toegevoegd.',
        duration: 5
      })
    }).catch(() => {
      message.error('Fout tijdens het toevoegen van keuringen!')
    }).finally(() => {
      this.saving = false
    })
  }

  selectAll (type:string) : void {
    if (type === 'blocks') {
      this.surveyData.block_ids = []
      this.blocks.forEach((block:BlockType) => {
        this.surveyData.block_ids.push(block.id)
      })
    } else if (type === 'buildings') {
      this.surveyData.building_ids = []
      this.buildings.forEach((building:BuildingType) => {
        this.surveyData.building_ids.push(building.id)
      })
    }
  }

  filterOptions (input:string, option:{componentOptions:{children:{text:string}[]}}) : boolean {
    return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }

  moment (dateTime:string, format:string) : moment.Moment {
    return moment(dateTime, format)
  }

  get requiredFieldsFilled () : boolean {
    if (this.surveyData.type === 'project') {
      return this.surveyData.project_id !== null && this.surveyData.survey_form_ids.length > 0 && this.surveyData.agree
    } else if (this.surveyData.type === 'block') {
      return this.surveyData.project_id !== null && this.surveyData.survey_form_ids.length > 0 && this.surveyData.block_ids.length > 0 && this.surveyData.agree
    } else if (this.surveyData.type === 'building') {
      return this.surveyData.project_id !== null && this.surveyData.survey_form_ids.length > 0 && this.surveyData.building_ids.length > 0 && this.surveyData.agree
    }

    return false
  }
}
